import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { IconHeadset, IconSend, IconMail } from '@tabler/icons-react';
import supportFeedbackApiService from '../../services/support-feedback-api.service';
import { useLoader } from '../../loader/LoaderContext';
import { useSelector } from 'react-redux';
import './SupportFeedback.css';

const FeedbackSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(2, 'First name is too short')
    .max(50, 'First name is too long')
    .required('First name field is required'),
  lastName: Yup.string()
    .min(2, 'Last name is too short')
    .max(50, 'Last name is too long')
    .required('Last name field is required'),
  queryType: Yup.string().required('Query type field is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email address field is required'),
  subject: Yup.string()
    .min(2, 'Subject is too short')
    .max(100, 'Subject is too long')
    .required('Subject field is required'),
  message: Yup.string()
    .min(10, 'Message must be at least 10 characters')
    .max(500, 'Message must be 500 characters or less')
    .required('Message field is required'),
});

function SupportFeedback() {
  const userAttributes = useSelector((state) => state.auth.userAttributes);

  const { showLoader, hideLoader } = useLoader();

  const handleSubmit = async (
    values,
    { setSubmitting, setStatus, resetForm },
  ) => {
    try {
      showLoader();
      await supportFeedbackApiService.submitUserFeedback({ ...values });

      toast.success('Your feedback has been submitted successfully!');

      resetForm();
    } catch (error) {
      setStatus({
        error:
          'There was an error submitting your feedback. Please try again later.',
      });
    } finally {
      hideLoader();
      setSubmitting(false);
    }
  };

  return (
    <div className="full-page-layout">
      <div className="innerpage detailed-page support-feedback-page">
        {/* Centered Icon */}
        <div className="support-icon-container">
          <IconHeadset size={40} />
        </div>

        {/* Page Header */}
        <div className="page-header">
          <h1>Support & Feedback</h1>
          <p className="page-subtitle">
            We value your input and are committed to improving your experience.
            Please complete the form below to share your feedback, request
            assistance, or report an issue. Our support team will respond promptly.
          </p>
        </div>

        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            queryType: 'general_feedback',
            email: userAttributes?.email,
            subject: '',
            message: '',
          }}
          validationSchema={FeedbackSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, status, isSubmitting, dirty, resetForm }) => (
            <div className="support-form-container">
              <Form>
                    {/* First Name & Last Name Row */}
                    <div className="support-feedback-form-row">
                      <div className="support-feedback-form-group">
                        <label htmlFor="firstName" className="support-feedback-label">
                          First Name<span className="support-feedback-required">*</span>
                        </label>
                        <Field
                          type="text"
                          id="firstName"
                          name="firstName"
                          autoComplete="off"
                          placeholder="Enter first name"
                          className={`support-feedback-input ${touched.firstName && errors.firstName ? 'error' : ''}`}
                        />
                        {touched.firstName && errors.firstName && (
                          <span className="support-feedback-error-message">
                            {errors.firstName}
                          </span>
                        )}
                      </div>

                      <div className="support-feedback-form-group">
                        <label htmlFor="lastName" className="support-feedback-label">
                          Last Name<span className="support-feedback-required">*</span>
                        </label>
                        <Field
                          type="text"
                          id="lastName"
                          name="lastName"
                          autoComplete="off"
                          placeholder="Enter last name"
                          className={`support-feedback-input ${touched.lastName && errors.lastName ? 'error' : ''}`}
                        />
                        {touched.lastName && errors.lastName && (
                          <span className="support-feedback-error-message">
                            {errors.lastName}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Category Dropdown */}
                    <div className="support-feedback-form-group">
                      <label htmlFor="queryType" className="support-feedback-label">
                        Select Your Query<span className="support-feedback-required">*</span>
                      </label>
                      <Field
                        as="select"
                        id="queryType"
                        name="queryType"
                        autoComplete="off"
                        className={`support-feedback-select ${touched.queryType && errors.queryType ? 'error' : ''}`}
                      >
                        <option value="" disabled>
                          Choose the category that best fits your request
                        </option>
                        <option value="general_feedback">General Feedback</option>
                        <option value="technical_issue">Technical Issue</option>
                        <option value="billing_inquiry">Billing Inquiry</option>
                        <option value="feature_request">Feature Request</option>
                        <option value="other">Other</option>
                      </Field>
                      {touched.queryType && errors.queryType && (
                        <span className="support-feedback-error-message">
                          {errors.queryType}
                        </span>
                      )}
                    </div>

                    {/* Email */}
                    <div className="support-feedback-form-group">
                      <label htmlFor="email" className="support-feedback-label">
                        Email Address<span className="support-feedback-required">*</span>
                      </label>
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        autoComplete="off"
                        placeholder="your.email@example.com"
                        readOnly
                        disabled
                        className={`support-feedback-input disabled ${touched.email && errors.email ? 'error' : ''}`}
                      />
                      <span className="support-feedback-helper-text">
                        Pre-filled with your account email
                      </span>
                      {touched.email && errors.email && (
                        <span className="support-feedback-error-message">
                          {errors.email}
                        </span>
                      )}
                    </div>

                    {/* Subject */}
                    <div className="support-feedback-form-group">
                      <label htmlFor="subject" className="support-feedback-label">
                        Subject<span className="support-feedback-required">*</span>
                      </label>
                      <Field
                        type="text"
                        id="subject"
                        name="subject"
                        autoComplete="off"
                        placeholder="Enter subject"
                        className={`support-feedback-input ${touched.subject && errors.subject ? 'error' : ''}`}
                      />
                      {touched.subject && errors.subject && (
                        <span className="support-feedback-error-message">
                          {errors.subject}
                        </span>
                      )}
                    </div>

                    {/* Message */}
                    <div className="support-feedback-form-group">
                      <label htmlFor="message" className="support-feedback-label">
                        Message<span className="support-feedback-required">*</span>
                      </label>
                      <Field
                        as="textarea"
                        id="message"
                        name="message"
                        autoComplete="off"
                        placeholder="Enter your message"
                        className={`support-feedback-textarea ${touched.message && errors.message ? 'error' : ''}`}
                      />
                      {touched.message && errors.message && (
                        <span className="support-feedback-error-message">
                          {errors.message}
                        </span>
                      )}
                    </div>

                    {/* Error Alert */}
                    {status && status.error && (
                      <div className="support-feedback-alert">
                        {status.error}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="support-feedback-actions">
                      <button
                        type="button"
                        className="support-feedback-btn support-feedback-btn-cancel"
                        onClick={() => resetForm()}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="support-feedback-btn support-feedback-btn-send"
                        disabled={isSubmitting || !dirty}
                      >
                        {isSubmitting ? 'Sending...' : (
                          <>
                            <IconSend size={18} />
                            Send
                          </>
                        )}
                      </button>
                    </div>
                  </Form>

                  {/* Footer */}
                  <div className="support-feedback-footer">
                    <p className="support-feedback-footer-text">
                      Need immediate assistance?
                    </p>
                    <a
                      href="mailto:support@prescriptsure.com"
                      className="support-feedback-email-link"
                    >
                      <IconMail size={18} className="support-feedback-email-icon" />
                      support@prescriptsure.com
                    </a>
                  </div>
                </div>
              )}
            </Formik>
      </div>
    </div>
  );
}

export default SupportFeedback;
